import { ApiError, Commercial, CommercialType } from '@talon/web/lib/api';
import { Box, BoxProps, Text } from 'grommet';
import { Video } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { SESSION_ESTIMATOR_TEMP_ESTIMATE_TITLE } from 'src/config';
import { useCommercialStore, useEstimateStore, useProjectStore } from 'src/context';
import { TalonCard } from '../TalonCard';
import { CommercialRow } from './CommercialRow';
import { CreateCommercialModal } from './CreateCommercialModal';

export type CommercialsPanelProps = {
  cardProps?: BoxProps;
};

export const CommercialsPanel = observer(({ cardProps }: CommercialsPanelProps) => {
  /** Context **/
  const commercialStore = useCommercialStore();
  const estimateStore = useEstimateStore();
  const projectStore = useProjectStore();
  const currentProjectId = projectStore.currentProject?.id;

  /** State **/
  const [showAddModal, setShowAddModal] = useState(false);
  const [commercials, setCommercials] = useState<Commercial[]>([]);
  const [isLoadingCommercials, setIsLoadingCommercials] = useState(true);
  const [selected, setSelected] = useState<string[]>([]);

  /** Computed **/
  const tempEstimate = estimateStore.tempEstimate;

  /** Methods **/

  const selectedNames = () => {
    const titles = selected.map((s) => commercials.find((item) => item.id === s)?.title).join(', ');
    return titles;
  };

  const chooseCommercial = (id: string) => {
    if (selected.includes(id)) {
      setSelected(selected.filter((s) => s !== id));
    } else {
      setSelected([...selected, id]);
    }
  };

  const createCommercial = async (type: CommercialType, durationInSeconds: number, title?: string) => {
    if (!type || typeof durationInSeconds !== 'number') return;
    if (!currentProjectId) {
      console.error('No current project ID');
      return;
    }

    const jobCode = projectStore.currentProject?.job_code ?? '';

    const commercial = await commercialStore.createCommercial({
      projectId: currentProjectId || '',
      jobCode,
      durationInSeconds,
      type,
      title: title || 'New Commercial',
    });

    if (commercial) {
      setCommercials([...commercials, commercial]);
      await estimateStore.createUsageEstimate(`TempEstimate_Usage_${commercial.id}`, commercial.id, true);
      await estimateStore.refreshTempEstimate(true);
    }
  };

  const deleteCommercials = async () => {
    if (!tempEstimate) {
      console.warn('Could not delete Sessions - Temp Estimate is undefined');
      return;
    }

    // delete the selected commercials (usage_estimates are cascade-deleted automatically)
    await Promise.all(selected.map((s) => commercialStore.deleteCommercial(s)));
    // remove the deleted commercials from the list
    setCommercials(commercials.filter((commercial) => !selected.includes(commercial.id)));
    setSelected([]);

    // Refresh the estimate totals after deletion
    await estimateStore.refreshEstimateTotals(estimateStore.tempEstimate?.id);
  };

  /** Effects **/

  useEffect(() => {
    const getCommercials = async () => {
      const projectId = projectStore.currentProject?.id;
      if (!projectId) {
        setCommercials([]);
        setIsLoadingCommercials(false);
        return;
      }

      setIsLoadingCommercials(true);

      // The commercials don't depend on the estimate, so list them alongside it rather than after
      // its chain of requests; each row fills in its costs once the estimate arrives
      const listCommercials = async () => {
        const s = await commercialStore.listCommercialsByProjectId(projectId);
        setCommercials(s || []);
        setIsLoadingCommercials(false);
      };

      const loadEstimate = async () => {
        // Get or create hidden Estimate for Usage Estimator page
        const tempTitle = `${SESSION_ESTIMATOR_TEMP_ESTIMATE_TITLE}${projectId}`;
        let estimate = await estimateStore.getEstimateByTitle(tempTitle, 'usage_estimates', true);
        if (!estimate) {
          await estimateStore.createEstimate(projectId, tempTitle, true);
          estimate = await estimateStore.getEstimateByTitle(tempTitle, 'usage_estimates', true);
        }
        if (estimate?.is_hidden) estimateStore.setTempEstimate(estimate);

        await estimateStore.refreshTempEstimate();
      };

      try {
        await Promise.all([listCommercials(), loadEstimate()]);
      } catch (err) {
        if ((err as ApiError).status !== 400) console.error(err);
        setIsLoadingCommercials(false);
      }
    };
    getCommercials();
  }, [projectStore.currentProject?.id]);

  /** Render **/

  return (
    <TalonCard
      label="Commercial"
      labelPlural={'Commercials'}
      newItem={() => setShowAddModal(true)}
      deleteItems={() => deleteCommercials()}
      selectedNames={selectedNames()}
      addIcon={<Video />}
      cardProps={cardProps}
    >
      <Box>
        {isLoadingCommercials ? (
          <Box pad="medium" align="center">
            <Text color="dark-3">Loading commercials…</Text>
          </Box>
        ) : commercials.length > 0 ? (
          commercials.map((commercial) => (
            <CommercialRow
              key={commercial.id}
              commercial={commercial}
              onChecked={() => chooseCommercial(commercial.id)}
              checked={selected.includes(commercial.id)}
            />
          ))
        ) : (
          <Box pad="medium" align="center">
            <Text color="dark-3">
              No Commercials found.{' '}
              <Box onClick={() => setShowAddModal(true)}>
                <Text weight={300} color="accent-5">
                  Click here to create one
                </Text>
              </Box>
              .
            </Text>
          </Box>
        )}
      </Box>
      {showAddModal && <CreateCommercialModal addCommercial={createCommercial} setShowModal={setShowAddModal} />}
    </TalonCard>
  );
});
